import {onBlockLoaded} from "@plaudit/frontend-apis";
import {BufferedEventManager} from "../shared/buffered-event-manager";

type ItemData = {event: {item_id?: string, item_name?: string, index: number}, reported?: boolean};
const itemDataMap = new WeakMap<Element, ItemData>();
function getItemData(element: Element) {
	const itemWrapper = element.closest(".plaudit-ga4-ec-for-wc-item-wrapper");
	return itemWrapper ? itemDataMap.get(itemWrapper) : undefined;
}

function makeEventReporter(listConfigEvent: ListConfig['event']) {
	return new BufferedEventManager<ItemData['event']>(buffer => window.gtag?.('event', 'view_item_list', {...listConfigEvent, items: buffer}));
}

/**
 * Visible fraction measured against min(card size, viewport size) per axis. For a card that fits in the
 * viewport, this equals intersectionRatio; for a card taller/wider than the viewport it becomes "how much of
 * the viewport does the visible slice fill", so min_intersection stays reachable no matter the card's size.
 */
function effectiveIntersectionRatio(entry: IntersectionObserverEntry): number {
	const rootHeight = entry.rootBounds?.height ?? window.innerHeight;
	const rootWidth = entry.rootBounds?.width ?? window.innerWidth;
	const denominator = Math.min(entry.boundingClientRect.height, rootHeight) * Math.min(entry.boundingClientRect.width, rootWidth);
	return denominator > 0 ? (entry.intersectionRect.width * entry.intersectionRect.height) / denominator : entry.intersectionRatio;
}

const listConfigHolderSelector = '[data-plaudit-ga4-e-commerce-for-woo-commerce-block-events*="view_item_list"]';
type ListConfig = {
	event: {currency: string, item_list_id?: string, item_list_name?: string},
	item_count: number, observer: IntersectionObserver, reporter: ReturnType<typeof makeEventReporter>
};
const listConfig = new WeakMap<Element, ListConfig|undefined>();
function getListConfig(element: Element): ListConfig|undefined {
	const owner = element.closest(listConfigHolderSelector);
	if (!owner) {
		return undefined;
	}

	let res = listConfig.get(owner);
	if (res) {
		return res;
	}

	const blockEvents = JSON.parse(owner.getAttribute('data-plaudit-ga4-e-commerce-for-woo-commerce-block-events') || "{}");
	if (!('view_item_list' in blockEvents) || typeof blockEvents['view_item_list'] !== 'object' || !blockEvents['view_item_list']) {
		return res;
	}

	const {event, config} = blockEvents['view_item_list'] as {event: ListConfig['event'], config: {firing_requirements: {min_intersection: number}}};
	const reporter = makeEventReporter(event);
	const minIntersection = config.firing_requirements.min_intersection;
	const observer = new IntersectionObserver(entries => {
		for (const entry of entries) {
			if (entry.isIntersecting && effectiveIntersectionRatio(entry) >= minIntersection) {
				const itemData = getItemData(entry.target);
				if (itemData) {
					reporter.enqueue(itemData);
				}
			}
		}
		// Per Claude, this is a sampling grid that is balanced to let the now-more-complex checks for intersection run often enough to catch overlaps without firing often enough to cause performance issues
	}, {root: null, threshold: [...new Set([...Array.from({length: 41}, (_, i) => i / 40), minIntersection])].sort((a, b) => a - b)});

	res = {event, item_count: 0, observer, reporter};
	listConfig.set(owner, res);
	return res;
}

function getItemEventProps(cfg: ListConfig) {
	return Object.fromEntries(Object.entries(cfg.event).filter(([k]) => k === 'item_list_id' || k === 'item_list_name'));
}

function sendSelectItemEvent(event: MouseEvent) {
	if (event.target instanceof HTMLElement) {
		const listConfig = getListConfig(event.target);
		const itemEvent = getItemData(event.target)?.event;
		if (listConfig && itemEvent) {
			window.gtag?.('event', 'select_item', {...getItemEventProps(listConfig), items: [itemEvent]});
		}
	}
}

function handleNewProductElement(scriptElement: HTMLElement) {
	const itemData = JSON.parse(scriptElement.textContent) as Record<string, unknown>;
	if (!('item_id' in itemData && itemData['item_id'] && typeof itemData['item_id'] === 'string') && !('item_name' in itemData && itemData['item_name'] && typeof itemData['item_name'] === 'string')) {
		return;
	}

	const blockRoot = scriptElement.parentElement;
	if (!blockRoot || itemDataMap.has(blockRoot)) {
		return;
	}
	const listConfig = getListConfig(blockRoot);
	if (!listConfig) {
		return;
	}

	blockRoot.classList.toggle("plaudit-ga4-ec-for-wc-item-wrapper", true);
	itemDataMap.set(blockRoot, {event: {...itemData, index: ++listConfig.item_count}, reported: false});
	listConfig.observer.observe(blockRoot);

	blockRoot.addEventListener('click', sendSelectItemEvent);
	blockRoot.addEventListener('auxclick', sendSelectItemEvent);
}

// language=JQuery-CSS
const scriptSelector = "script.plaudit-ga4-ec-for-wc-item-data[type='application/json']";

const itemDataObserver = new MutationObserver(mutations => {
	for (const mutation of mutations) {
		for (const addedNode of mutation.addedNodes) {
			if (addedNode instanceof HTMLElement) {
				if (addedNode.classList.contains("plaudit-ga4-ec-for-wc-item-data")) {
					handleNewProductElement(addedNode);
				} else {
					for (const scriptElement of document.querySelectorAll<HTMLScriptElement>(scriptSelector)) {
						handleNewProductElement(scriptElement);
					}
				}
			}
		}
	}
});

onBlockLoaded({
	targetSelector: listConfigHolderSelector,
	onAdd(blockRoot) {
		const listConfig = getListConfig(blockRoot);
		if (!listConfig) {
			return;
		}
		itemDataObserver.observe(blockRoot, {subtree: true, childList: true});
		for (const element of blockRoot.querySelectorAll<HTMLScriptElement>(scriptSelector)) {
			handleNewProductElement(element);
		}
	}
});
